import { InputProps } from 'front-commons/ds/components/Input/interfaces';
import { UseFetchProps } from 'front-commons/hooks/useFetch/interfaces';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { findMyPharmaciesNoHandler, getUserSimulatedPosSuggestions } from 'services/pos';
import useCustomer from 'stores/customer';
import usePos from 'stores/pos';
import { Pagination } from 'front-commons/ds';
import { isCNPJSearchTerm } from 'shared/cnpj';

interface UsePosSearch<T>
	extends Pick<
		UseFetchProps<T, any>,
		'onSuccess' | 'onError' | 'paginationParams' | 'handleModifyParams' | 'fetchOnRender'
	> {
	statusList?: Array<'PENDING' | 'READY' | 'BLOCKED' | 'REJECTED'>;
	refetchOnClear?: boolean;
	onPaginate?(): void;
	onSearchClean?(): void;
}

export default function usePosSearch<T = any>({
	statusList,
	fetchOnRender = false,
	refetchOnClear,
	paginationParams,
	onError,
	onSuccess,
	onPaginate,
	onSearchClean,
	handleModifyParams,
}: UsePosSearch<T>) {
	const { handleIncrementToMyPos } = usePos();
	const { simulatedViewType } = useCustomer();
	const isSimulated = !!simulatedViewType();

	const [helpText, setHelpText] = useState<string | undefined>(undefined);
	const [inputValue, setInputValue] = useState<string | undefined>(undefined);

	const [commonLoading, setCommonLoading] = useState(false);
	const [simulatedLoading, setSimulatedLoading] = useState(false);

	const [filter, setFilter] =
		useState<Partial<Record<'page' | 'size' | 'tradeName' | 'cnpj' | 'statusList', unknown>>>();

	const handlePaginate = (page: number) => {
		setFilter((prevState) => ({
			...prevState,
			page,
		}));
		onPaginate?.();
	};

	const [pagesCount, setPagesCount] = useState(0);

	const handleInputChange = (el: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(el.currentTarget.value);

		if (helpText) setHelpText(undefined);
	};

	const handleSearchWithFilters = async () => {
		try {
			setCommonLoading(true);

			const params = filter || {};

			handleModifyParams?.(params);

			if (statusList) params.statusList = statusList;

			const response = await findMyPharmaciesNoHandler({
				page: paginationParams?.initialPage || 0,
				...params,
				size: paginationParams?.size || 20,
			});

			handleIncrementToMyPos(response?.content.pointOfSaleList || []);
			setPagesCount(response?.totalPages || 0);
			onSuccess?.(response as T);
		} catch (error) {
			onError?.(error);
		} finally {
			setCommonLoading(false);
		}
	};

	const handleSearchWithCNPJ = async () => {
		try {
			setSimulatedLoading(true);

			const response = await getUserSimulatedPosSuggestions({ cnpj: (filter?.cnpj as string) || '' });

			handleIncrementToMyPos([
				...response.map((pos: any) => ({
					...pos,
					role: 'OWNER' as any,
					status: 'READY' as any,
				})),
			]);
			setPagesCount(0);
			onSuccess?.(response as T);
		} catch (error) {
			onError?.(error);
		} finally {
			setSimulatedLoading(false);
		}
	};

	const searchType = useMemo(() => {
		if (inputValue === '') {
			onSearchClean?.();
			setInputValue(undefined);
			if (refetchOnClear && filter) setFilter(undefined);
		}

		if (inputValue && inputValue.length > 1) {
			if (isCNPJSearchTerm(inputValue)) return 'cnpj';
			return 'tradeName';
		}

		return 'tradeName';
	}, [inputValue]);

	const handleSearch = useCallback(() => {
		if (!inputValue) return;

		if (isSimulated || searchType === 'cnpj') {
			const cnpj = CNPJ(inputValue).unmask;

			if (cnpj.length !== 14) return setHelpText('Digite o CNPJ completo');

			return setFilter({ cnpj });
		}

		setFilter({ tradeName: inputValue });
	}, [inputValue, searchType]);

	const inputProps: Omit<InputProps, 'name'> = {
		type: searchType === 'cnpj' ? 'cnpj' : 'text',
		value: inputValue,
		loading: commonLoading || simulatedLoading,
		helpText,
		rightIcon: {
			name: 'search',
			color: '--text-link',
			onClick: handleSearch,
		},
		onChange: handleInputChange,
		onPressEnter: handleSearch,
	};

	const hasRendered = useRef(false);

	useEffect(() => {
		if (!fetchOnRender && !hasRendered.current) {
			hasRendered.current = true;
			return;
		}

		(isSimulated ? handleSearchWithCNPJ : handleSearchWithFilters)();
	}, [filter]);

	return {
		search: inputValue,
		loading: inputProps.loading || false,
		inputProps,
		Pagination:
			pagesCount && pagesCount > 1 ? (
				<Pagination
					page={(filter?.page as number) || 0}
					waiting={inputProps.loading}
					disabled={inputProps.loading}
					totalPages={pagesCount}
					onPaginate={handlePaginate}
				/>
			) : null,
	};
}
